// Top-level hash dispatcher. #/ landing | #/app/* owner | #/b|m|o/* booking | #/demo* | static pages.
import { Button, Logo } from './ui';
import { t } from './i18n';
import { useRoute, Link } from './app-owner/router';
import { useEffect, useState } from './app-owner/react';
import { useLocale } from './AppShell';

/**
 * Route-level code splitting. Each branch is a dynamic import(), so the public booking page does not ship the owner
 * app (and vice versa). Loaded modules are cached, so a revisit renders on the first frame.
 */
function lazyRoute<P extends Record<string, any>>(load: () => Promise<any>, pick: (m: any) => any) {
  let Cached: any = null;
  let pending: Promise<void> | null = null;
  const start = (): Promise<void> => (pending ??= load().then((m) => { Cached = pick(m); }, (err) => { pending = null; throw err; }));
  function Lazy(props: P) {
    const [, force] = useState(0);
    const [failed, setFailed] = useState(false);
    const go = () => { setFailed(false); start().then(() => force((n) => n + 1), () => setFailed(true)); };
    useEffect(() => {
      if (Cached) return undefined;
      go();
      const back = () => { if (!Cached) go(); };
      window.addEventListener('online', back);
      return () => window.removeEventListener('online', back);
    }, []);
    if (!Cached && failed) {
      return (
        <main id="main" tabIndex={-1} className="pl-screen ow-center" data-testid="route-offline">
          <div className="ow-notfound pl-card pl-stack">
            <Logo />
            <h1 className="pl-h2">{t('owner.owner.offline.title')}</h1>
            <p className="pl-muted">{t('owner.owner.offline.sub')}</p>
            <Button size="lg" block onClick={go} data-testid="route-offline-retry">{t('common.btn.retry')}</Button>
          </div>
        </main>
      );
    }
    return Cached ? <Cached {...props} /> : (
      <main id="main" tabIndex={-1} className="pl-screen" aria-busy="true"><h1 className="pb-skel__h1">{t('common.a11y.loading')}</h1></main>
    );
  }
  return Lazy;
}

const BookingApp = lazyRoute(() => import('./app-booking'), (m) => m.BookingApp);
const Landing = lazyRoute(() => import('./app-owner/screens/Landing'), (m) => m.Landing);
const OwnerApp = lazyRoute(() => import('./app-owner/OwnerApp'), (m) => m.OwnerApp);
const DemoHub = lazyRoute(() => import('./demo/DemoHub'), (m) => m.DemoHub);
const MockCheckout = lazyRoute<{ id: string }>(() => import('./demo/MockCheckout'), (m) => m.MockCheckout);
const UiGallery = lazyRoute(() => import('./ui/Gallery'), (m) => m.UiGallery);
const A11yStatement = lazyRoute<{ standalone?: boolean }>(() => import('./app-owner/screens/Legal'), (m) => m.A11yStatement);
const TermsPage = lazyRoute(() => import('./app-owner/screens/PublicPages'), (m) => m.TermsPage);
const SupportPage = lazyRoute(() => import('./app-owner/screens/PublicPages'), (m) => m.SupportPage);
const DeleteAccountInfo = lazyRoute(() => import('./app-owner/screens/PublicPages'), (m) => m.DeleteAccountPage);
const PrivacyPage = lazyRoute<{ standalone?: boolean }>(() => import('./app-owner/screens/Legal'), (m) => m.PrivacyPage);

export function NotFound() {
  return (
    <main id="main" tabIndex={-1} className="pl-screen ow-center" data-testid="not-found">
      <div className="ow-notfound pl-card pl-stack">
        <Logo />
        <h1 className="pl-h2">{t('owner.owner.404.title')}</h1>
        <p className="pl-muted">{t('owner.owner.404.sub')}</p>
        <Button size="lg" block href="#/">{t('owner.owner.404.cta')}</Button>
      </div>
    </main>
  );
}

export function Routes() {
  useLocale();
  const route = useRoute();
  const [a, b, c] = route.segments;
  if (!a) return <Landing />;
  if (a === 'app') return <OwnerApp />;
  if (a === 'b' || a === 'm' || a === 'o') return <BookingApp />;
  if (a === 'demo') {
    if (!b) return <DemoHub />;
    if (b === 'ui') return <UiGallery />;
    if (b === 'pay' && c) return <MockCheckout id={c} />;
    return <NotFound />;
  }
  if (a === 'accessibility') return <A11yStatement standalone />;
  if (a === 'terms') return <TermsPage />;
  if (a === 'support') return <SupportPage />;
  if (a === 'delete-account') return <DeleteAccountInfo />;
  if (a === 'privacy') return <PrivacyPage standalone />;
  return <NotFound />;
}
export { Link };
